import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { CloseIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import WelcomeSection from "components/WelcomeSection";
import { ReactTable } from "components/ReactTable";
import { useTrackingRoutes, type TrackingRoute, type RouteUpsertPayload } from "hooks/useTrackingRoutes";
import { showToast, toastTypes } from "utils";

export default function TrackingRoutesPage() {
  const toast = useToast();
  const { routes, isLoading, error, create, update, remove } = useTrackingRoutes();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const cancelRef = useRef<any>(null);

  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editing, setEditing] = useState<TrackingRoute | null>(null);
  const [formValues, setFormValues] = useState<RouteUpsertPayload>({ name: "", driver_id: null, school_id: null, is_active: true });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<TrackingRoute | null>(null);
  const [searchValue, setSearchValue] = useState("");

  const resetForm = useCallback(() => {
    setFormValues({ name: "", driver_id: null, school_id: null, is_active: true });
    setEditing(null);
    setFormMode("create");
  }, []);

  const openCreate = useCallback(() => { resetForm(); setFormMode("create"); onOpen(); }, [onOpen, resetForm]);
  const openEdit = useCallback((row: TrackingRoute) => {
    setEditing(row);
    setFormMode("edit");
    setFormValues({ name: row.name, driver_id: row.driver_id ?? null, school_id: row.school_id ?? null, is_active: row.is_active });
    onOpen();
  }, [onOpen]);

  const handleSubmit = useCallback(async () => {
    if (!formValues.name.trim()) {
      showToast({ toast, message: "Le nom est obligatoire.", status: toastTypes.error });
      return;
    }
    setIsSubmitting(true);
    try {
      const payload: RouteUpsertPayload = { name: formValues.name.trim(), driver_id: formValues.driver_id ?? undefined, school_id: formValues.school_id ?? undefined, is_active: formValues.is_active };
      if (formMode === "create") {
        await create(payload);
        showToast({ toast, message: "Route créée.", status: toastTypes.success });
      } else if (editing) {
        await update(editing.id, payload);
        showToast({ toast, message: "Route mise à jour.", status: toastTypes.success });
      }
      onClose();
      resetForm();
    } catch (e: any) {
      showToast({ toast, message: e?.response?.data?.detail || e?.message || "Erreur.", status: toastTypes.error });
    } finally {
      setIsSubmitting(false);
    }
  }, [create, update, editing, formMode, formValues, onClose, resetForm, toast]);

  const handleDeleteClick = useCallback((row: TrackingRoute) => { setActiveItem(row); onConfirmOpen(); }, [onConfirmOpen]);
  const handleDeleteConfirm = useCallback(async () => {
    if (!activeItem) return;
    setDeletingId(activeItem.id);
    try {
      await remove(activeItem.id);
      showToast({ toast, message: "Route supprimée.", status: toastTypes.success });
      onConfirmClose();
      setActiveItem(null);
    } catch (e: any) {
      showToast({ toast, message: e?.response?.data?.detail || "Erreur.", status: toastTypes.error });
    } finally {
      setDeletingId(null);
    }
  }, [activeItem, remove, toast, onConfirmClose]);

  const filteredData = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return routes;
    return routes.filter((r) => r.name.toLowerCase().includes(q));
  }, [routes, searchValue]);

  const columns = useMemo(() => {
    const col = createColumnHelper<TrackingRoute>();
    return [
      col.accessor("name", { header: "Nom", cell: (info) => <Text>{info.getValue()}</Text> }),
      col.accessor("driver_id", { header: "Conducteur (ID)", cell: (info) => <Text fontSize="sm">{info.getValue() ?? "—"}</Text> }),
      col.accessor("school_id", { header: "École (ID)", cell: (info) => <Text fontSize="sm">{info.getValue() ?? "—"}</Text> }),
      col.accessor("is_active", { header: "Statut", cell: (info) => <Badge colorScheme={info.getValue() ? "green" : "gray"}>{info.getValue() ? "Actif" : "Inactif"}</Badge> }),
      col.display({
        id: "action",
        header: "Action",
        cell: ({ row }) => (
          <HStack>
            <IconButton aria-label="Modifier" size="sm" icon={<EditIcon />} onClick={() => openEdit(row.original)} />
            <IconButton aria-label="Supprimer" size="sm" icon={<DeleteIcon />} colorScheme="red" onClick={() => handleDeleteClick(row.original)} isLoading={deletingId === row.original.id} />
          </HStack>
        ),
      }),
    ];
  }, [openEdit, handleDeleteClick, deletingId]);

  return (
    <>
      <Flex gap="18px" bg="very_light_gray" flex={1} flexDirection="column" py="20px">
        <WelcomeSection />
        <Flex flexDirection="column" alignItems="start" px={{ base: "20px", sm: "24px" }} minHeight="100vh">
          <Flex justifyContent="space-between" alignItems="center" w="100%" flexWrap="wrap" gap={4}>
            <Heading size="text2xl" as="h5" fontFamily="Arimo">Routes</Heading>
            <Button size="xl" variant="outline" colorScheme="red_origin" onClick={openCreate}>Créer une route</Button>
          </Flex>
          {error && <Box mt={4} p={4} bg="red.50" borderRadius="md"><Text color="red.700">Erreur chargement.</Text></Box>}
          <Box mt={4} bg="white" w="100%" borderRadius="10px">
            <Flex p={4}><InputGroup w={{ base: "100%", sm: "300px" }}>
              <Input placeholder="Rechercher" value={searchValue} onChange={(e) => setSearchValue(e.target.value)} />
              <InputRightElement>{searchValue ? <IconButton aria-label="Effacer" size="sm" icon={<CloseIcon />} onClick={() => setSearchValue("")} /> : null}</InputRightElement>
            </InputGroup></Flex>
            {isLoading && !filteredData.length ? <Flex justify="center" py={10}><Spinner size="lg" /></Flex> : <ReactTable columns={columns as any} data={filteredData} size="sm" />}
            {!isLoading && !filteredData.length && <Box p={8} textAlign="center"><Text color="gray.600">Aucune route.</Text></Box>}
          </Box>
        </Flex>
      </Flex>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{formMode === "create" ? "Ajouter une route" : "Modifier la route"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3}>
              <FormControl isRequired><FormLabel>Nom</FormLabel><Input value={formValues.name} onChange={(e) => setFormValues((p) => ({ ...p, name: e.target.value }))} /></FormControl>
              <FormControl><FormLabel>ID conducteur (optionnel)</FormLabel><Input type="number" value={formValues.driver_id ?? ""} onChange={(e) => setFormValues((p) => ({ ...p, driver_id: e.target.value ? parseInt(e.target.value, 10) : null }))} /></FormControl>
              <FormControl><FormLabel>ID école (optionnel)</FormLabel><Input type="number" value={formValues.school_id ?? ""} onChange={(e) => setFormValues((p) => ({ ...p, school_id: e.target.value ? parseInt(e.target.value, 10) : null }))} /></FormControl>
              <FormControl><Flex align="center"><FormLabel mb={0}>Actif</FormLabel><Switch isChecked={formValues.is_active} onChange={(e) => setFormValues((p) => ({ ...p, is_active: e.target.checked }))} /></Flex></FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Annuler</Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={isSubmitting}>Enregistrer</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Supprimer la route</AlertDialogHeader>
            <AlertDialogBody>Êtes-vous sûr de vouloir supprimer cette route ?</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>Annuler</Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={deletingId === activeItem?.id} ml={3}>Supprimer</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
